import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

const Spaces = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);

  // Categorie: Spazi Interni, Spazi Esterni, Attività
  const categories = [
    {
      nameKey: 'category1',
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/3sc660db_1000087944.jpg',
          caption: 'Aula principale con tavolo circolare'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/8wxjjvzg_1000087929.jpg',
          caption: 'Angolo lettura multilingue'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/l4hlt50a_1000087942.jpg',
          caption: 'Finestra panoramica con materiali didattici'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/u2x4pazw_1000087923.jpg',
          caption: 'Spazio eventi e festività'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/u6olpwzg_1000001657.jpg',
          caption: 'Spogliatoio e area accoglienza'
        }
      ]
    },
    {
      nameKey: 'category2',
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/ry9xko24_1000001679.jpg',
          caption: 'Parco Pantano - Natura e avventura'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/i0lxj44w_1000087922.jpg',
          caption: 'Giardinaggio sul terrazzo'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/dc8r8xzr_1000087921.jpg',
          caption: 'Lettura all\'aperto nel bosco'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/t31k8ekf_1000089477.jpg',
          caption: 'Passeggiate educative in città'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/wjtm6041_1000089471.jpg',
          caption: 'Giardino privato con piscine'
        }
      ]
    },
    {
      nameKey: 'category3',
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/zg64egx0_1000001756.webp',
          caption: 'Attività sensoriale con materiali naturali'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/30cuzi7m_1000089481.jpg',
          caption: 'Gioco con blocchi di legno'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/h7rrxiac_1000089470.jpg',
          caption: 'Vita pratica - Lavare i piatti'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_nido-pantano/artifacts/183nqj60_1000087939.jpg',
          caption: 'Laboratorio creativo - Pittura'
        }
      ]
    }
  ];

  return (
    <section id="spaces" className="py-20 bg-gradient-to-br from-green-50 to-purple-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('spaces.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('spaces.subtitle')}
          </p>
        </div>

        {categories.map((category, catIndex) => (
          <div key={catIndex} className="mb-12">
            <div className="flex items-center justify-center mb-6">
              <Badge className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-2 text-lg font-semibold">
                {t(`spaces.${category.nameKey}`)}
              </Badge>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {category.images.map((image, imgIndex) => (
                <Card 
                  key={imgIndex}
                  className="border-0 shadow-xl overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <CardContent className="p-0">
                    <div className="relative h-72 overflow-hidden">
                      <img 
                        src={image.url}
                        alt={image.caption}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 text-white">
                          <p className="font-semibold">{image.caption}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-900" />
          </button>
          <div className="max-w-6xl max-h-[90vh]">
            <img 
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-center mt-4 text-lg">
              {selectedImage.caption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Spaces;
